import type FreeBusyRow from '../schema/FreeBusyRow';
import { emptyFreeBusyRow } from '../schema/FreeBusyRow';
import { getAttendeeData } from '../selectors/attendeeSelectors';
import { getSessionMailboxInfo } from '../selectors/sessionSelectors';
import { ensureAttendeeAddedToSession } from '../actions/ensureAttendeeAddedToSession';
import { slotsPerPage } from './pageUtils';
import FreeBusyType from '../schema/FreeBusyType';
import { getAvailabilityForSingleAddress } from './getAvailabilityForSingleAddress';
import type { PageQueryResponse } from 'owa-availability-service/lib/utils/getAvailabilityFunction';
import { setAttendeeWorkingHoursPropsMutator } from '../mutators/attendeeMutators';
import { getAttendeeMruCache } from '../store/attendeeMruCache';
import { logUsage } from 'owa-analytics';
import { isGetScheduleEnabled } from 'owa-feature-flags';
import { convertGetScheduleWorkingHoursToWorkingHoursType } from 'owa-calendar-scheduleitem-converters';
import { updateScheduleItemIdsForCache } from './scheduleItemIdUtils';
import { getAttendeeKey } from './getAttendeeKey';
import type { MailboxInfo } from 'owa-client-types';
/* eslint-disable-next-line @typescript-eslint/no-restricted-imports  -- (https://aka.ms/OWALintWiki)
 * Using transaction to mitigate a perf issue; this should be refactored to use idiomatic
 * Satchel/MobX patterns */
import { transaction } from 'mobx';

export function tryGetFreeBusy(
    sessionId: string,
    attendee: string,
    pageIndex: number
): FreeBusyRow | undefined {
    let page = undefined;

    const mailboxInfo = getSessionMailboxInfo(sessionId);

    if (!mailboxInfo) {
        return page;
    }

    const attendeeKey = getAttendeeKey(attendee, mailboxInfo);

    ensureAttendeeAddedToSession(sessionId, attendeeKey);

    const attendeeMruCache = getAttendeeMruCache(attendeeKey);

    if (!attendeeMruCache) {
        return page;
    }

    const dictionaryKey = pageIndex.toString();

    page = attendeeMruCache.get(dictionaryKey);
    if (!page) {
        // Initialize a full page with Loading
        attendeeMruCache.add(dictionaryKey, {
            mergedFreeBusy: emptyFreeBusyRow(slotsPerPage.get(), FreeBusyType.Loading),
            scheduleId: '',
        });

        // Request new data from server
        getAvailabilityForSingleAddress(
            attendee,
            pageIndex,
            mailboxInfo,
            (pageQueryResponse: PageQueryResponse) => {
                handlePageQueryResponse(pageIndex, mailboxInfo, pageQueryResponse);
            } /* processResponse */,
            (pageQueryResponse: PageQueryResponse) => {
                handleWorkPlansPageQueryResponse(mailboxInfo, pageQueryResponse);
            } /* processWorkPlansResponse */
        );

        page = attendeeMruCache.get(dictionaryKey);
    }

    return page;
}

function handlePageQueryResponse(
    pageIndex: number,
    mailboxInfo: MailboxInfo,
    pageQueryResponse: PageQueryResponse
) {
    transaction(() => {
        Object.keys(pageQueryResponse).forEach(address => {
            const attendeeKey = getAttendeeKey(address, mailboxInfo);
            const attendeeData = getAttendeeData(attendeeKey);
            const attendeeMruCache = getAttendeeMruCache(attendeeKey);

            // It is possible that the request returns after a session has
            // ended, so throw away the response if the attendee is no longer
            // being tracked by any consumers
            if (!attendeeData || attendeeData.sessions.length == 0 || !attendeeMruCache) {
                return;
            }

            const userAvailability = pageQueryResponse[address];

            const { availabilityView, workingHours, flexibleWorkingHours, error } =
                userAvailability;

            const isGatewayEnabled = isGetScheduleEnabled();

            // Not every error is actually a problem; for example, a 401 Unauthorized is expected
            // if an attendee is not sharing their f/b status, but we should errors regardless.
            if (error) {
                /* eslint-disable-next-line owa-custom-rules/forbid-specific-functions-patterns-inside-loops -- (https://aka.ms/OWALintWiki)
                 * Baseline, this function can't be used inside a loop, please move it outside
                 *	> Function 'logUsage' matches forbidden pattern (/logUsage/) and should not be used inside loops */
                logUsage('FreeBusyDataError', {
                    isGatewayEnabled,
                    responseCode: error.responseCode,
                });
            }

            const convertedLegacyWorkingHours = workingHours
                ? convertGetScheduleWorkingHoursToWorkingHoursType(workingHours)
                : undefined;

            if (convertedLegacyWorkingHours || flexibleWorkingHours) {
                setAttendeeWorkingHoursPropsMutator(
                    attendeeKey,
                    convertedLegacyWorkingHours,
                    flexibleWorkingHours ?? undefined
                );
            }

            const freeBusyRow: FreeBusyRow = {
                ...userAvailability,
                mergedFreeBusy: availabilityView
                    ? availabilityView.split('').map((value: string) => parseInt(value))
                    : emptyFreeBusyRow(slotsPerPage.get(), FreeBusyType.NoData),
            };

            if (isGatewayEnabled && !!freeBusyRow.scheduleItems) {
                updateScheduleItemIdsForCache(freeBusyRow.scheduleItems);
            }

            attendeeMruCache.add(pageIndex.toString(), freeBusyRow);
        });
    });
}

function handleWorkPlansPageQueryResponse(
    mailboxInfo: MailboxInfo,
    pageQueryResponse: PageQueryResponse
) {
    transaction(() => {
        Object.keys(pageQueryResponse).forEach(address => {
            const attendeeKey = getAttendeeKey(address, mailboxInfo);
            const attendeeData = getAttendeeData(attendeeKey);

            // It is possible that the request returns after a session has
            // ended, so throw away the response if the attendee is no longer
            // being tracked by any consumers
            if (
                !attendeeData ||
                attendeeData.sessions.length == 0 ||
                !getAttendeeMruCache(attendeeKey)
            ) {
                return;
            }

            const { flexibleWorkingHours, error } = pageQueryResponse[address];

            const isGatewayEnabled = isGetScheduleEnabled();

            // Not every error is actually a problem; for example, a 401 Unauthorized is expected
            // if an attendee is not sharing their f/b status, but we should errors regardless.
            if (error) {
                /* eslint-disable-next-line owa-custom-rules/forbid-specific-functions-patterns-inside-loops -- (https://aka.ms/OWALintWiki)
                 * Baseline, this function can't be used inside a loop, please move it outside
                 *	> Function 'logUsage' matches forbidden pattern (/logUsage/) and should not be used inside loops */
                logUsage('FreeBusyDataError', {
                    isGatewayEnabled,
                    responseCode: error.responseCode,
                });
            }

            if (flexibleWorkingHours) {
                setAttendeeWorkingHoursPropsMutator(
                    attendeeKey,
                    undefined /* workingHours */,
                    flexibleWorkingHours
                );
            }
        });
    });
}
